import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  background-color: #011748;
  color: #04abce;
  font-family: 'dos', monospace;
  user-select: none;
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  justify-content: space-between;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
  border-bottom: 2px solid #04abce;
  padding-bottom: 8px;
`;

const MonthYearText = styled.div`
  font-size: 1.1rem;
  color: #e4ce01;
  font-weight: bold;
  text-transform: uppercase;
`;

const NavButton = styled.button`
  background: #004f8a;
  color: #fffd46;
  border: 2px solid #00c4ff;
  font-family: 'dos', monospace;
  font-size: 1rem;
  padding: 2px 8px;
  cursor: pointer;
  
  &:hover {
    background-color: #325e81;
  }
  
  &:active {
    background-color: #011748;
  }
`;

const WeekdaysRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  width: 100%;
  text-align: center;
  font-weight: bold;
  color: #e4ce01;
  margin-bottom: 6px;
  font-size: 0.8rem;
`;

const DaysGrid = styled.div<{ $rows: number }>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(${props => props.$rows}, 1fr);
  width: 100%;
  gap: 3px;
  flex: 1;
`;

interface DayCellProps {
  isToday: boolean;
  isEmpty: boolean;
}

const DayCell = styled.div<DayCellProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 0.9rem;
  border: ${props => props.isEmpty ? 'none' : '1px solid #004f8a'};
  color: ${props => props.isToday ? '#011748' : '#04abce'};
  background-color: ${props => props.isToday ? '#e4ce01' : 'transparent'};
  font-weight: ${props => props.isToday ? 'bold' : 'normal'};
  
  &:hover {
    ${props => !props.isEmpty && !props.isToday && `
      background-color: #004f8a;
      cursor: pointer;
      color: #fffd46;
    `}
  }
`;

export default function RetroCalendar() {
  const now = new Date();
  
  const [viewDate, setViewDate] = useState(new Date(now.getFullYear(), now.getMonth(), 1));

  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const firstDayIndex = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handlePrevMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const cells = [];
  // Empty cells for alignment before first day of month
  for (let i = 0; i < firstDayIndex; i++) {
    cells.push(<DayCell key={`empty-${i}`} isEmpty={true} isToday={false} />);
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const isToday = 
      viewDate.getFullYear() === now.getFullYear() && 
      viewDate.getMonth() === now.getMonth() && 
      day === now.getDate();
      
    cells.push(
      <DayCell key={`day-${day}`} isEmpty={false} isToday={isToday}>
        {day}
      </DayCell>
    );
  }

  return (
    <Container>
      <HeaderRow>
        <NavButton onClick={handlePrevMonth}>&lt;</NavButton>
        <MonthYearText>
          {monthNames[viewDate.getMonth()]} {viewDate.getFullYear() - 30}
        </MonthYearText>
        <NavButton onClick={handleNextMonth}>&gt;</NavButton>
      </HeaderRow>
      
      <WeekdaysRow>
        <div>SU</div>
        <div>MO</div>
        <div>TU</div>
        <div>WE</div>
        <div>TH</div>
        <div>FR</div>
        <div>SA</div>
      </WeekdaysRow>
      
      <DaysGrid $rows={Math.ceil((firstDayIndex + daysInMonth) / 7)}>
        {cells}
      </DaysGrid>
    </Container>
  );
}
